import {Box, Collapse, ScaleFade, Spinner, useDisclosure, useTheme, VStack} from '@chakra-ui/react'
import {faChevronDown} from '@fortawesome/pro-regular-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import isEmpty from 'lodash/isEmpty'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'
import {useEffect, useState} from 'react'
import {SUPPORTED_LANGUAGES} from '../../../constants/languages'
import {ProductDescriptionSkillFormData} from '../../../dataModels/SkillFormData'
import axios from '../../../fetchers/axiosAPI'
import {useAuth} from '../../../hooks/useAuth'
import {useSkill} from '../../../hooks/useSkill'
import APIOpenModalFooter from '../../Modules/APIOpenModalFooter'
import SkillOutput from '../SkillOutput'
import SkillProductDescriptionForm from './SkillProductDescriptionForm'

const LoginModal = dynamic(() => import('../../Modals/LoginModal'))
const ToxicWarningLight = dynamic(() => import('../../Modules/ToxicWarningLight'))
const GenerationLimitReachedModal = dynamic(
  () => import('../../Modals/GenerationLimitReachedModal')
)

const SkillProductDescriptionPlayground = () => {
  const toxicWarning = useDisclosure()
  const limitReached = useDisclosure()
  const connectionNeeded = useDisclosure()

  const {skill, skillGroup} = useSkill()

  const theme = useTheme()
  const router = useRouter()
  const selectedLanguage = router.locale ?? 'en'

  const {isLoading, setIsLoading, isToxic, setIsToxic} = useSkill()

  const [output, setOutput] = useState<string>('')
  const [formInputs, setFormInputs] = useState<ProductDescriptionSkillFormData | null>(null)

  const {publicUser} = useAuth().state

  useEffect(() => {
    isToxic ? toxicWarning.onOpen() : toxicWarning.onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToxic])

  const generateContent = async (values: ProductDescriptionSkillFormData) => {
    if (!publicUser || !publicUser.APIKey) {
      setFormInputs(values)
      connectionNeeded.onOpen()
      return
    }
    setIsToxic(false)
    setIsLoading(true)
    setOutput('')

    const response = await axios
      .post(skill.endpoint, {
        ...values,
        textLanguage: selectedLanguage,
        locale: router.locale,
      })
      .catch(async (e: any) => {
        if (e.response.status === 403) {
          limitReached.onOpen()
        } else {
          const showToast = await import('../../../utils/showToast').then(mod => mod.showToast)
          showToast(e.response.data.error, 'error')
        }
      })
    if (response && response.data.error) {
      const showToast = await import('../../../utils/showToast').then(mod => mod.showToast)
      showToast(response.data.error, 'error')
    }

    if (response && response.data.output) {
      setIsToxic(response.data.isToxic)
      setOutput(response.data.output)
    }
    setIsLoading(false)
  }

  return (
    <>
      {connectionNeeded.isOpen && (
        <LoginModal
          isOpen={connectionNeeded.isOpen}
          onClose={connectionNeeded.onClose}
          inputsToRecover={formInputs}
          textLanguageToRecover={selectedLanguage as SUPPORTED_LANGUAGES}
        />
      )}
      <VStack
        w='full'
        p={5}
        spacing={1}
        pt={10}
        background={theme.colors.layers.appLevel1}
        borderRadius={theme.radii.big}
        justify='start'>
        <SkillProductDescriptionForm onGenerating={generateContent} setOutput={setOutput} />
        <ScaleFade in={isLoading} initialScale={0.8} unmountOnExit>
          <Spinner size='md' color={theme.colors.brand.greyLight} />
        </ScaleFade>
        <Collapse in={!isEmpty(output)} style={{width: '100%'}} unmountOnExit animateOpacity>
          <VStack w='full' spacing={5}>
            <Box color={theme.colors.text.basic}>
              <FontAwesomeIcon icon={faChevronDown} />
            </Box>
            <SkillOutput output={output} input='' />
          </VStack>
        </Collapse>
        {toxicWarning.isOpen && (
          <Box w='full'>
            <Collapse in={toxicWarning.isOpen} unmountOnExit animateOpacity>
              <ToxicWarningLight onClose={toxicWarning.onClose} />
            </Collapse>
          </Box>
        )}
        <APIOpenModalFooter service={skill} serviceGroup={skillGroup} />
      </VStack>
      {limitReached.isOpen && (
        <GenerationLimitReachedModal isOpen={limitReached.isOpen} onClose={limitReached.onClose} />
      )}
    </>
  )
}

export default SkillProductDescriptionPlayground
